import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import ServicesSection from './components/ServicesSection';
import QuoteRequest from './components/QuoteRequest';
import ReviewUpload from './components/ReviewUpload';
import ShipmentTracking from './components/ShipmentTracking';
import ProTips from './components/ProTips';
import PastProjects from './components/PastProjects';
import TeamMembers from './components/TeamMembers';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://lonetrs.com';

const operationsSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Operations Center | LONE TRS LLC',
  description:
    'Manage your transportation and relocation needs with LONE TRS LLC. Request a quote, track shipments, explore services, and connect with our expert team. Serving Las Vegas to the West Coast.',
  url: `${SITE_URL}/operations`,
  isPartOf: { '@type': 'WebSite', name: 'LONE TRS LLC', url: SITE_URL },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Operations', item: `${SITE_URL}/operations` },
    ],
  },
  mainEntity: {
    '@type': 'Service',
    serviceType: 'Transportation & Relocation',
    provider: {
      '@type': 'MovingCompany',
      name: 'LONE TRS LLC',
      telephone: '+1-415-888-5336',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Las Vegas',
        addressRegion: 'NV',
        addressCountry: 'US',
      },
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'LONE TRS Services',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Residential Moving' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Commercial Moving' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Interstate Moving' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Vehicle Transport' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'General Freight' } },
      ],
    },
  },
};

export default function OperationsPage() {
  return (
    <div className="min-h-screen bg-dark-900">
      <title>Operations Center | Services, Quotes &amp; Tracking | LONE TRS LLC Las Vegas</title>
      <meta
        name="description"
        content="Request a free quote, track your shipment, and explore all LONE TRS LLC transportation services — residential, commercial, interstate moving, vehicle transport &amp; freight in Las Vegas &amp; West Coast."
      />
      <meta
        name="keywords"
        content="moving quote Las Vegas, shipment tracking Nevada, transportation services request, relocation services operations, freight quote West Coast"
      />
      <link rel="canonical" href={`${SITE_URL}/operations`} />
      <meta property="og:title" content="Operations Center | LONE TRS LLC" />
      <meta
        property="og:description"
        content="Request quotes, track shipments, and manage your transportation needs with LONE TRS LLC — Las Vegas to West Coast."
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${SITE_URL}/operations`} />
      <meta name="twitter:card" content="summary_large_image" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(operationsSchema) }}
      />

      <Navigation />
      
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-32">
        <div className="absolute inset-0 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-20 w-96 h-96 bg-electric-blue/20 rounded-full blur-3xl animate-pulse-glow" />
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-electric-cyan/20 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1s' }} />
          </div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h1 className="font-orbitron text-6xl md:text-7xl font-bold text-white mb-6 tracking-wider">
            OPERATIONS <span className="text-electric-blue">CENTER</span>
          </h1>
          <p className="font-inter text-white/70 text-xl leading-relaxed">
            Manage your transportation needs, track shipments, and access all our services in one place
          </p>
        </div>
      </section>

      <ServicesSection />
      <QuoteRequest />
      <ShipmentTracking />
      <ReviewUpload />
      <ProTips />
      <PastProjects />
      <TeamMembers />
      <Footer />
    </div>
  );
}
